import React, { useEffect, useRef, useState } from 'react'
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive'
import NotificationsOffIcon from '@material-ui/icons/NotificationsOff'
import RefreshIcon from '@material-ui/icons/Refresh'

import Widget from '../../../components/Widget'
import {
    createReminderInterval,
    deleteReminderInterval,
    getCanonicalManagementResponse,
    updateReminderInterval,
    useManagementReminderIntervals,
} from '../../../apis'
import { useApiHttpState } from '../../../context/ApiContext'
import {
    createIconActionName,
    DestructiveConfirmationDialog,
    FieldValidationError,
    fieldErrorAttributes,
    SectionStateBoundary,
    useSnackbarSectionInteraction,
} from '../primitives'

const SECTION = 'reminderIntervals'
const SECTION_ID = 'reminder-intervals'

export const reminderIntervalLabel = daysBefore => {
    const days = Number(daysBefore)
    if (days === 0) return 'Day of Reminder'
    return `${days} ${days === 1 ? 'day' : 'days'} before`
}

const validDaysBefore = value => /^\d+$/.test(String(value).trim()) && Number.isSafeInteger(Number(value))

const canonicalResult = async resultPromise => {
    const result = await resultPromise
    if (Array.isArray(result) && result[0]) return result
    const response = Array.isArray(result) ? result[1] : result
    const payload = response && Object.prototype.hasOwnProperty.call(response, 'data')
        ? response.data
        : response
    if (payload && payload.section === SECTION && typeof payload.versionToken === 'string' && Array.isArray(payload.records)) {
        return payload
    }
    return getCanonicalManagementResponse(result, SECTION)
}

const conflictGuidance = error => {
    if (!error) return ''
    if (error.code === 'DUPLICATE_INTERVAL') {
        return 'Choose a different number of days; an interval with this timing already exists.'
    }
    if (error.code === 'REMINDER_HISTORY_EXISTS') {
        return 'This interval has reminder history and cannot be deleted. Deactivate it instead.'
    }
    return ''
}

const sortedIntervals = data => [...data.records].sort(
    (left, right) => Number(right.daysBefore) - Number(left.daysBefore) || Number(left.id) - Number(right.id),
)

export default function ReminderIntervalsSection() {
    const source = useManagementReminderIntervals()
    const { api } = useApiHttpState()
    const interaction = useSnackbarSectionInteraction(source.managementSection || null)
    const initialSourceRef = useRef(source.managementSection)
    const initialErrorRef = useRef(null)
    const headingRef = useRef(null)
    const destructiveOpenerRef = useRef(null)
    const [form, setForm] = useState(null)
    const [localFieldError, setLocalFieldError] = useState('')
    const [destructiveAction, setDestructiveAction] = useState(null)

    const { state } = interaction
    const confirmed = state.confirmedData
    const versionToken = confirmed && confirmed.versionToken

    useEffect(() => {
        if (source.managementSection && initialSourceRef.current !== source.managementSection) {
            initialSourceRef.current = source.managementSection
            interaction.load(() => source.managementSection)
        } else if (source.isError && initialErrorRef.current !== source.isError && !state.confirmedData) {
            initialErrorRef.current = source.isError
            interaction.load(() => Promise.reject(source.isError))
        }
    }, [source.managementSection, source.isError, state.confirmedData, interaction.load])

    const refreshCanonical = () => canonicalResult(source.revalidate())
    const retryLoad = () => interaction.load(refreshCanonical)
    const refresh = () => interaction.refresh(refreshCanonical)

    const openCreate = () => {
        const draft = { mode: 'create', id: null, daysBefore: '', active: true }
        setForm(draft)
        setLocalFieldError('')
        interaction.setDraft(draft)
        interaction.clearFeedback()
    }

    const openEdit = interval => {
        const draft = {
            mode: 'edit',
            id: interval.id,
            daysBefore: String(interval.daysBefore),
            active: interval.active,
        }
        setForm(draft)
        setLocalFieldError('')
        interaction.setDraft(draft)
        interaction.clearFeedback()
    }

    const submitMutation = ({ mutation, draft, mutationKey, successMessage, retainDraft = false }) =>
        interaction.submit({
            mutation: () => canonicalResult(mutation()),
            refresh: refreshCanonical,
            draft,
            mutationKey,
            successMessage,
            retainDraft,
        })

    const submitForm = async event => {
        event.preventDefault()
        if (!validDaysBefore(form.daysBefore)) {
            setLocalFieldError('Enter a whole number greater than or equal to 0.')
            return
        }
        setLocalFieldError('')
        const payload = { daysBefore: Number(form.daysBefore), active: Boolean(form.active) }
        const mutation = form.mode === 'create'
            ? () => createReminderInterval(api, payload, versionToken)
            : () => updateReminderInterval(api, form.id, payload, versionToken)
        const result = await submitMutation({
            mutation,
            draft: form,
            mutationKey: form.mode === 'create' ? 'create' : form.id,
            successMessage: form.mode === 'create' ? 'Reminder interval created.' : 'Reminder interval updated.',
            retainDraft: true,
        })
        if (result.ok) {
            setForm(null)
            interaction.setDraft(null)
        }
    }

    const activate = interval => submitMutation({
        mutation: () => updateReminderInterval(api, interval.id, { active: true }, versionToken),
        draft: null,
        mutationKey: interval.id,
        successMessage: `${reminderIntervalLabel(interval.daysBefore)} activated.`,
    })

    const confirmDestructive = () => {
        const { kind, interval } = destructiveAction
        if (kind === 'deactivate') {
            return submitMutation({
                mutation: () => updateReminderInterval(api, interval.id, { active: false }, versionToken),
                draft: null,
                mutationKey: interval.id,
                successMessage: `${reminderIntervalLabel(interval.daysBefore)} deactivated.`,
            })
        }
        return submitMutation({
            mutation: () => deleteReminderInterval(api, interval.id, versionToken),
            draft: null,
            mutationKey: interval.id,
            successMessage: `${reminderIntervalLabel(interval.daysBefore)} deleted.`,
        })
    }

    const openDestructive = (kind, interval, event) => {
        destructiveOpenerRef.current = event.currentTarget
        interaction.clearFeedback()
        setDestructiveAction({ kind, interval })
    }

    const disabled = !interaction.canSubmit
    const daysBeforeError = localFieldError || state.fieldErrors.daysBefore
    const guidance = conflictGuidance(state.mutationError)

    return (
        <section aria-labelledby={`${SECTION_ID}-heading`}>
            <Widget
                disableWidgetMenu
                header={
                    <React.Fragment>
                        <Typography
                            ref={headingRef}
                            id={`${SECTION_ID}-heading`}
                            component="h2"
                            variant="h5"
                            color="textSecondary"
                            tabIndex={-1}
                        >
                            Reminder Intervals
                        </Typography>
                        <div>
                            <IconButton
                                type="button"
                                aria-label="Refresh Reminder Intervals"
                                onClick={refresh}
                                disabled={interaction.isPending || !confirmed}
                            >
                                <RefreshIcon />
                            </IconButton>
                            <Button
                                type="button"
                                color="primary"
                                variant="outlined"
                                startIcon={<AddIcon />}
                                onClick={openCreate}
                                disabled={disabled}
                            >
                                Add reminder interval
                            </Button>
                        </div>
                    </React.Fragment>
                }
            >

            <SectionStateBoundary
                state={state}
                sectionName="Reminder Intervals"
                emptyMessage="No reminder intervals are configured."
                onRetryLoad={retryLoad}
                onRetryRefresh={refresh}
                onAcceptStale={interaction.acceptStale}
            >
                {data => (
                    <Table aria-label="Reminder Intervals">
                        <caption>Reminder timing and active state</caption>
                        <TableHead>
                            <TableRow>
                                <TableCell>Interval</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedIntervals(data).map(interval => {
                                const label = reminderIntervalLabel(interval.daysBefore)
                                return (
                                    <TableRow key={interval.id}>
                                        <TableCell>{label}</TableCell>
                                        <TableCell>{interval.active ? 'Active' : 'Inactive'}</TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                type="button"
                                                aria-label={createIconActionName('Edit', label)}
                                                onClick={() => openEdit(interval)}
                                                disabled={disabled}
                                            >
                                                <EditIcon />
                                            </IconButton>
                                            {interval.active ? (
                                                <IconButton
                                                    type="button"
                                                    aria-label={createIconActionName('Deactivate', label)}
                                                    onClick={event => openDestructive('deactivate', interval, event)}
                                                    disabled={disabled}
                                                >
                                                    <NotificationsOffIcon />
                                                </IconButton>
                                            ) : (
                                                <IconButton
                                                    type="button"
                                                    aria-label={createIconActionName('Activate', label)}
                                                    onClick={() => activate(interval)}
                                                    disabled={disabled}
                                                >
                                                    <NotificationsActiveIcon />
                                                </IconButton>
                                            )}
                                            <IconButton
                                                type="button"
                                                aria-label={createIconActionName('Delete', label)}
                                                onClick={event => openDestructive('delete', interval, event)}
                                                disabled={disabled}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                )}
            </SectionStateBoundary>

            {guidance && <p role="alert">{guidance}</p>}

            {form && (
                <Dialog
                    open
                    onClose={interaction.isPending ? undefined : () => {
                        setForm(null)
                        setLocalFieldError('')
                        interaction.setDraft(null)
                    }}
                    aria-labelledby={`${SECTION_ID}-editor-title`}
                    fullWidth
                    maxWidth="sm"
                >
                    <DialogTitle id={`${SECTION_ID}-editor-title`}>
                        {form.mode === 'create' ? 'Add reminder interval' : `Edit ${reminderIntervalLabel(form.daysBefore)}`}
                    </DialogTitle>
                    <form aria-label={`${form.mode === 'create' ? 'Create' : 'Edit'} reminder interval`} onSubmit={submitForm}>
                        <DialogContent>
                            <TextField
                                autoFocus
                                fullWidth
                                id={`${SECTION_ID}-daysBefore`}
                                name="daysBefore"
                                type="number"
                                label="Days before appointment"
                                value={form.daysBefore}
                                onChange={event => {
                                    const next = { ...form, daysBefore: event.target.value }
                                    setForm(next)
                                    interaction.setDraft(next)
                                    setLocalFieldError('')
                                }}
                                inputProps={{ min: 0, step: 1, ...fieldErrorAttributes({
                                    sectionId: SECTION_ID,
                                    fieldName: 'daysBefore',
                                    error: daysBeforeError,
                                }) }}
                            />
                            <FieldValidationError
                                sectionId={SECTION_ID}
                                fieldName="daysBefore"
                                error={daysBeforeError}
                            />
                            {form.mode === 'create' && (
                                <FormControlLabel
                                    control={(
                                        <Checkbox
                                            checked={form.active}
                                            onChange={event => {
                                                const next = { ...form, active: event.target.checked }
                                                setForm(next)
                                                interaction.setDraft(next)
                                            }}
                                            name="active"
                                            color="primary"
                                        />
                                    )}
                                    label="Active"
                                />
                            )}
                        </DialogContent>
                        <DialogActions>
                            <Button
                                type="button"
                                onClick={() => {
                                    setForm(null)
                                    setLocalFieldError('')
                                    interaction.setDraft(null)
                                }}
                                disabled={interaction.isPending}
                            >
                                Cancel
                            </Button>
                            <Button type="submit" color="primary" variant="contained" disabled={disabled}>
                                {form.mode === 'create' ? 'Create interval' : 'Save interval'}
                            </Button>
                        </DialogActions>
                    </form>
                </Dialog>
            )}
            </Widget>

            <DestructiveConfirmationDialog
                open={Boolean(destructiveAction)}
                title={destructiveAction && destructiveAction.kind === 'delete'
                    ? 'Delete reminder interval?'
                    : 'Deactivate reminder interval?'}
                recordLabel={destructiveAction
                    ? reminderIntervalLabel(destructiveAction.interval.daysBefore)
                    : 'Reminder interval'}
                consequence={destructiveAction && destructiveAction.kind === 'delete'
                    ? 'This permanently removes the interval and its pending reminders. Intervals with reminder history must be deactivated instead.'
                    : 'This removes pending reminders for this interval. Existing reminder history is preserved.'}
                confirmLabel={destructiveAction && destructiveAction.kind === 'delete'
                    ? 'Delete interval'
                    : 'Deactivate interval'}
                onConfirm={confirmDestructive}
                onClose={() => setDestructiveAction(null)}
                onConfirmed={() => setDestructiveAction(null)}
                openerRef={destructiveOpenerRef}
                sectionHeadingRef={headingRef}
            />
        </section>
    )
}
